import React from 'react';

import {DomainSequenceType} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import {
  DOMAIN_INTERFACE_ID_BY_SEQUENCE_TYPE,
  getDomainSequenceType,
} from 'antibodies/domain-annotation/domainSchemaInterfaces';
import isDomainSequenceAnnotationEnabled from 'antibodies/domain-annotation/isDomainSequenceAnnotationEnabled';
import promptAndAnnotateDomainSequences from 'antibodies/domain-annotation/promptAndAnnotateDomainSequences';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {TagSchema} from 'constants/RegistryConstants';
import FileStates from 'FileStates';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import * as BioentityActions from 'inventory/BioentityActions';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {
  TagSchemaInterfacesQuery,
  TagSchemaInterfacesQueryVariables,
} from 'schemas/util/__generated/TagSchemaInterfacesQuery';
import {TAG_SCHEMA_INTERFACES_QUERY} from 'schemas/util/getTagSchemaInterfaceIds';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export const NOT_REGISTERED_TOOLTIP = 'Register this entity to annotate it';
export const VALIDATION_ERRORS_TOOLTIP = 'Resolve registry validation errors to annotate this entity';

/** Refetches the sequence if it is open, so that its page shows the annotations and schema field values. */
function refreshOpenSequence(sequenceId: string, sequenceType: DomainSequenceType): void {
  if (sequenceType === DomainSequenceType.AA) {
    if (AaSequenceStore.getAaSequenceById(sequenceId) != null) {
      AaSequenceActions.open(sequenceId, {});
    }
    return;
  }
  const fileStates = FileStates.get();
  if (fileStates.get(sequenceId) == null) {
    return;
  }
  fileStates.updateSequenceVersions([sequenceId], {showHistoryPanel: false});
  // The open DNA sequence keeps its schema field values in a collection that refetching the sequence doesn't reset.
  BioentityActions.getTags(sequenceId);
}

export interface AnnotateDomainSequenceButtonProps {
  fileId: string;
  tagSchema: Pick<TagSchema, 'id' | 'itemType'> | null;
  isEditing: boolean;
  areTagsEditable: boolean;
  isRegistered: boolean;
  schemaValidationState: SchemaValidationState | null;
  /** Prefills the folder for the annotation results dataset. */
  projectFolderId: string | null;
}

function getDisabledReason(
  isRegistered: boolean,
  schemaValidationState: SchemaValidationState | null
): string | null {
  if (!isRegistered) {
    return NOT_REGISTERED_TOOLTIP;
  }
  if (isFailed(schemaValidationState)) {
    return VALIDATION_ERRORS_TOOLTIP;
  }
  return null;
}

/**
 * Button that annotates an AA or DNA domain sequence from its metadata tab. Renders nothing unless the sequence's
 * schema implements the domain schema interface for its sequence type and the sequence is editable.
 * Disabled, with a tooltip explaining why, until the sequence is registered and free of registry validation errors.
 */
export default function AnnotateDomainSequenceButton({
  fileId,
  tagSchema,
  isEditing,
  areTagsEditable,
  isRegistered,
  schemaValidationState,
  projectFolderId,
}: AnnotateDomainSequenceButtonProps): JSX.Element | null {
  const sequenceType = tagSchema == null ? null : getDomainSequenceType(tagSchema.itemType);
  const isEligibleSequence =
    isDomainSequenceAnnotationEnabled() && sequenceType != null && areTagsEditable && !isEditing;
  const interfacesResult = useSkippableBenchlingQuery<
    TagSchemaInterfacesQuery,
    TagSchemaInterfacesQueryVariables
  >(TAG_SCHEMA_INTERFACES_QUERY, {
    skip: !isEligibleSequence || tagSchema == null,
    variables: {id: tagSchema?.id ?? ''},
    fetchPolicy: 'cache-first',
  });

  if (
    !isEligibleSequence ||
    sequenceType == null ||
    interfacesResult.type !== QueryResultType.SUCCESS ||
    interfacesResult.data.tagSchema.__typename !== 'TagSchemaNode' ||
    !interfacesResult.data.tagSchema.interfaces.some(
      (schemaInterface) => schemaInterface.id === DOMAIN_INTERFACE_ID_BY_SEQUENCE_TYPE[sequenceType]
    )
  ) {
    return null;
  }
  const disabledReason = getDisabledReason(isRegistered, schemaValidationState);
  return (
    <Tooltip text={disabledReason} isDisabled={disabledReason == null}>
      <Button
        size={ButtonSize.MINI}
        buttonStyle={ButtonStyle.PRIMARY}
        text="Annotate"
        disabled={disabledReason != null}
        aria-disabled={disabledReason != null}
        onClick={async () => {
          await promptAndAnnotateDomainSequences({
            sequenceIds: [fileId],
            sequenceType,
            initialFolderId: projectFolderId,
            actionOrigin: ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE,
            onAnnotated: () => {
              refreshOpenSequence(fileId, sequenceType);
            },
          });
        }}
        dataTestAttrs={{component: 'AnnotateDomainSequenceButton', element: 'button'}}
      />
    </Tooltip>
  );
}
